/**
 * Sign In Page using Clerk
 *
 * Uses Clerk's pre-built SignIn component with custom styling
 */

import { SignIn } from '@clerk/clerk-react';
import { DevTools } from '@/components/dev/DevTools';
import { Sparkles, ArrowRight } from 'lucide-react';

export default function ClerkSignIn() {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Hero Section - Hidden on mobile, left side on desktop */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative bg-gradient-to-br from-primary via-primary/95 to-primary/90 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20 py-12">
          {/* Logo */}
          <div
            className="flex items-center gap-3 mb-12 animate-fade-in-up animate-on-load"
            style={{ animationFillMode: 'forwards' }}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-lg shadow-accent/30">
              <Sparkles className="h-6 w-6" />
            </div>
            <span className="font-heading text-2xl font-bold text-white">RenovationAI</span>
          </div>

          {/* Hero Text */}
          <h1 className="font-heading text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-6">
            Transform spaces
            <br />
            <span className="text-accent">with AI precision</span>
          </h1>
          <p className="text-lg xl:text-xl text-white/80 max-w-md mb-8 leading-relaxed">
            Generate instant renovation quotes and stunning visualizations. Turn leads into clients
            faster than ever.
          </p>

          {/* Feature List */}
          <div className="space-y-3">
            {['AI-powered room analysis', 'Instant quote generation', 'Professional visualizations'].map(
              (feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 text-white/90 animate-fade-in-up animate-on-load"
                  style={{ animationDelay: `${150 + index * 100}ms`, animationFillMode: 'forwards' }}
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-accent/20">
                    <ArrowRight className="h-4 w-4 text-accent" />
                  </div>
                  <span className="text-base font-medium">{feature}</span>
                </div>
              )
            )}
          </div>

          {/* Hero Image */}
          <div
            className="mt-12 relative animate-fade-in-up animate-on-load"
            style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
          >
            <div className="aspect-[4/3] max-w-lg rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
              <img
                src="/images/hero-kitchen-transform.png"
                alt="AI-powered kitchen renovation transformation"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-y-0 left-1/2 w-0.5 bg-white/60" />
            </div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-accent/20 rounded-full blur-3xl" />
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
          </div>
        </div>
      </div>

      {/* Sign In Form Section */}
      <div className="flex-1 flex flex-col min-h-screen lg:min-h-0">
        {/* Mobile Header */}
        <div
          className="lg:hidden flex items-center justify-center gap-2.5 pt-10 pb-6 px-4 animate-fade-in-up"
          style={{ animationDuration: '0.5s' }}
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-md shadow-primary/20">
            <Sparkles className="h-5 w-5" />
          </div>
          <span className="font-heading text-xl font-bold text-foreground">RenovationAI</span>
        </div>

        {/* Clerk SignIn Component */}
        <div className="flex-1 flex items-center justify-center px-5 py-4 sm:px-6 sm:py-8 lg:px-8 bg-background relative">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.02] via-transparent to-primary/[0.02]" />
          <SignIn
            routing="hash"
            signUpUrl="/sign-up"
            forceRedirectUrl="/dashboard"
            appearance={{
              variables: {
                // BUG-004 FIX: Use Clerk's colorPrimary variable for button background
                // This overrides Clerk's internal styling that was making buttons transparent
                colorPrimary: '#148068', // hsl(166, 76%, 32%) - matches --accent
                colorPrimaryText: '#ffffff', // white text on buttons
              },
              elements: {
                rootBox: 'w-full max-w-[400px]',
                card: 'shadow-2xl shadow-primary/10 border-border/40',
                headerTitle: 'font-heading text-2xl sm:text-3xl font-bold',
                headerSubtitle: 'text-muted-foreground',
                socialButtonsBlockButton:
                  'h-11 sm:h-12 text-base font-medium border-border/60 hover:bg-muted/50',
                formFieldInput:
                  'h-11 sm:h-12 text-base px-4 bg-muted/30 border-border/50 focus:bg-background',
                formButtonPrimary:
                  'h-11 sm:h-12 text-base font-semibold shadow-md',
                footerActionLink: 'font-semibold text-accent hover:text-accent/80',
              },
            }}
          />
        </div>

        {/* Mobile Footer */}
        <div className="lg:hidden py-5 text-center">
          <p className="text-sm text-muted-foreground">AI-powered renovation visualization</p>
        </div>
      </div>

      {/* Dev Tools */}
      <DevTools />
    </div>
  );
}
